/**
 * Planned Customer Self-Scan HTTP paths (W3/W4).
 * ApiClient prefixes `/api/{tenant}/v1` → full URL
 * `/api/{tenant}/v1/customer/self-scan/...`.
 */

export const SELF_SCAN_API_ROOT = '/customer/self-scan' as const;

export const selfScanApiPaths = {
  /** POST — start or resume ACTIVE basket + editor lease. */
  session: `${SELF_SCAN_API_ROOT}/baskets/start`,
  /** GET — basket snapshot (+ lines when wired). */
  basket: (publicId: string): string =>
    `${SELF_SCAN_API_ROOT}/baskets/${encodeURIComponent(publicId)}`,
  /** POST — scan-add barcode (fail-closed on lease). */
  scan: (publicId: string): string =>
    `${SELF_SCAN_API_ROOT}/baskets/${encodeURIComponent(publicId)}/scan`,
  /** PATCH — update line qty. */
  lineQty: (publicId: string, lineId: number): string =>
    `${SELF_SCAN_API_ROOT}/baskets/${encodeURIComponent(publicId)}/lines/${lineId}`,
  /** DELETE — remove line. */
  lineRemove: (publicId: string, lineId: number): string =>
    `${SELF_SCAN_API_ROOT}/baskets/${encodeURIComponent(publicId)}/lines/${lineId}`,
  /** POST — lock for payment (PAYMENT_LOCKED). */
  lock: (publicId: string): string =>
    `${SELF_SCAN_API_ROOT}/baskets/${encodeURIComponent(publicId)}/lock`,
  /** POST — renew editor lease (FR-04). */
  leaseRenew: (publicId: string): string =>
    `${SELF_SCAN_API_ROOT}/baskets/${encodeURIComponent(publicId)}/lease/renew`,
  /** POST — release editor lease. */
  leaseRelease: (publicId: string): string =>
    `${SELF_SCAN_API_ROOT}/baskets/${encodeURIComponent(publicId)}/lease/release`,
  /** POST — CreateCheckoutSessionFromSelfScanBasket handoff. */
  checkoutHandoff: (publicId: string): string =>
    `${SELF_SCAN_API_ROOT}/baskets/${encodeURIComponent(publicId)}/checkout-handoff`,
  /** GET — customer basket history (PAID / ABANDONED / ARCHIVED). */
  history: `${SELF_SCAN_API_ROOT}/history`,
  /**
   * GET — selective capability for FR-17 (W6 settings).
   */
  settings: `${SELF_SCAN_API_ROOT}/settings`,
} as const;

/** Customer UI deep-link / resume path shapes (returnTarget allowlist). */
export function buildSelfScanPath(
  tenantCode: string,
  options?: {
    readonly salesPointId?: number | null;
    readonly basketPublicId?: string | null;
  },
): string {
  const params = new URLSearchParams();
  if (
    typeof options?.salesPointId === 'number' &&
    Number.isFinite(options.salesPointId) &&
    options.salesPointId > 0
  ) {
    params.set('salesPointId', String(options.salesPointId));
  }
  if (
    options?.basketPublicId !== undefined &&
    options.basketPublicId !== null &&
    options.basketPublicId.trim().length > 0
  ) {
    params.set('basketPublicId', options.basketPublicId.trim());
  }
  const qs = params.toString();
  const base = `/${encodeURIComponent(tenantCode)}/self-scan`;
  return qs.length > 0 ? `${base}?${qs}` : base;
}

/** Legacy Skenovat entry with Self-Scan SoT (`?selfScan=1`). */
export function buildShopScannerSelfScanPath(
  tenantCode: string,
  options?: {
    readonly salesPointId?: number | null;
    readonly kioskId?: number | null;
  },
): string {
  const params = new URLSearchParams();
  params.set('selfScan', '1');
  if (
    typeof options?.salesPointId === 'number' &&
    Number.isFinite(options.salesPointId) &&
    options.salesPointId > 0
  ) {
    params.set('salesPointId', String(options.salesPointId));
  } else if (
    typeof options?.kioskId === 'number' &&
    Number.isFinite(options.kioskId) &&
    options.kioskId > 0
  ) {
    params.set('kioskId', String(options.kioskId));
  }
  return `/${encodeURIComponent(tenantCode)}/shop/scanner?${params.toString()}`;
}
